/** StudentsPanel — Admin students CRUD & parent link. Extracted from panels.jsx. */
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Link2, Pencil } from "lucide-react";
import { Req } from "./_shared";

export function StudentsPanel({ students, users, schools, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { school_id: schools[0]?.id || "", reg_id: "", name: "", grade: "5", section: "A", gender: "male", dob: "", parent_email: "", parent_phone: "", avatar_url: "" };
  const [form, setForm] = useState(emptyForm);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null);
  const [linkForm, setLinkForm] = useState({ parent_email: "", parent_name: "", parent_phone: "" });

  useEffect(() => { if (!form.school_id && schools[0]) setForm((f) => ({ ...f, school_id: schools[0].id })); }, [schools]);

  const parents = users.filter((u) => u.role === "parent");

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm, school_id: schools[0]?.id || "" }); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      school_id: s.school_id, reg_id: s.reg_id, name: s.name, grade: s.grade, section: s.section || "",
      gender: s.gender || "male", dob: s.dob || "", parent_email: s.parent_email || "", parent_phone: s.parent_phone || "",
      avatar_url: s.avatar_url || "",
    });
    setOpen(true);
  };

  const save = async () => {
    const missing = [];
    if (!form.school_id) missing.push("School");
    if (!form.reg_id?.trim()) missing.push("Reg ID");
    if (!form.name?.trim()) missing.push("Name");
    if (!form.grade?.trim()) missing.push("Grade");
    if (missing.length) {
      toast.error(`Please fill required field${missing.length > 1 ? "s" : ""}: ${missing.join(", ")}`);
      return;
    }
    try {
      if (editing) await http.put(`/students/${editing.id}`, form);
      else await http.post("/students", form);
      toast.success(editing ? "Student updated" : "Student added");
      setOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this student and all their screening data?")) return;
    try { await http.delete(`/students/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const openLink = (s) => {
    setLinkTarget(s);
    setLinkForm({ parent_email: s.parent_email || "", parent_name: "", parent_phone: s.parent_phone || "" });
    setLinkOpen(true);
  };
  const submitLink = async () => {
    if (!linkForm.parent_email?.trim()) {
      toast.error("Parent email is required to link");
      return;
    }
    try {
      await http.post("/users/link-parent", { student_id: linkTarget.id, ...linkForm });
      toast.success("Parent linked");
      setLinkOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Link failed"); }
  };

  const [search, setSearch] = useState("");
  const filtered = students.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.name.toLowerCase().includes(q) || s.kid_id.toLowerCase().includes(q) || (s.parent_email || "").toLowerCase().includes(q);
  });

  return (
    <>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="font-heading font-semibold text-lg">Students ({students.length})</h2>
          <div className="text-xs text-slate-500">Create, edit, link to parents.</div>
        </div>
        <div className="flex items-center gap-2">
          <Input data-testid="students-search" placeholder="Search by name, KID ID or parent email…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-72 h-9" />
          <Button data-testid="add-student-btn" onClick={openCreate}><Plus className="w-4 h-4 mr-1" /> New student</Button>
        </div>
      </div>
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-students-table">
            <thead className="text-slate-400 uppercase text-[10px] tracking-widest bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3">KID ID</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Class</th>
                <th className="text-left px-4 py-3">Sex</th>
                <th className="text-left px-4 py-3">DOB</th>
                <th className="text-left px-4 py-3">Parent</th>
                <th className="text-left px-4 py-3">Points</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 100).map((s) => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 font-mono text-slate-500">{s.kid_id}</td>
                  <td className="px-4 py-2.5 font-semibold text-slate-800">{s.name}</td>
                  <td className="px-4 py-2.5 text-slate-600">{s.grade}{s.section}</td>
                  <td className="px-4 py-2.5 text-slate-600 capitalize text-xs">{s.gender || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs font-mono">{s.dob || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600 text-xs">{s.parent_email || <span className="text-rose-500">— unlinked —</span>}</td>
                  <td className="px-4 py-2.5 tabular-nums font-semibold text-sky-700">{s.points?.toLocaleString?.() || 0}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" data-testid={`link-parent-${s.id}`} title="Link parent" onClick={() => openLink(s)}>
                      <Link2 className="w-4 h-4 text-sky-500" />
                    </Button>
                    <Button size="icon" variant="ghost" data-testid={`edit-student-${s.id}`} title="Edit" onClick={() => openEdit(s)}>
                      <Pencil className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button size="icon" variant="ghost" data-testid={`delete-student-${s.id}`} title="Delete" onClick={() => del(s.id)}>
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length > 100 && <div className="p-3 text-xs text-slate-400 text-center">Showing first 100 of {filtered.length} — refine search to see more.</div>}
      </CardContent></Card>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="student-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit student" : "New student"}</DialogTitle>
            <DialogDescription>KID ID is auto-generated as SCHOOL.LOCATION.REG</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>School<Req /></Label>
              <select data-testid="student-school-select" value={form.school_id} onChange={(e) => setForm({ ...form, school_id: e.target.value })} className="mt-1 w-full h-10 border border-slate-200 rounded-md px-3 bg-white text-sm">
                {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div><Label>Reg ID<Req /></Label><Input data-testid="student-reg-input" value={form.reg_id} onChange={(e) => setForm({ ...form, reg_id: e.target.value })} placeholder="PL11500" className="mt-1" /></div>
            <div><Label>Name<Req /></Label><Input data-testid="student-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Grade<Req /></Label><Input data-testid="student-grade-input" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} className="mt-1" /></div>
            <div><Label>Section</Label><Input data-testid="student-section-input" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} className="mt-1" /></div>
            <div>
              <Label>Sex</Label>
              <select data-testid="student-gender-select" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="mt-1 w-full h-10 border border-slate-200 rounded-md px-3 bg-white text-sm">
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="any">Prefer not to say</option>
              </select>
            </div>
            <div><Label>Date of Birth</Label><Input data-testid="student-dob-input" type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="mt-1" /></div>
            <div className="col-span-2">
              <Label>Parent email <span className="text-slate-400 text-[11px]">(optional — you can link a parent later)</span></Label>
              <Input data-testid="student-parent-email-input" list="parent-emails" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} className="mt-1" placeholder="parent@example.com" />
              <datalist id="parent-emails">
                {parents.map((p) => <option key={p.id} value={p.email}>{p.name}</option>)}
              </datalist>
            </div>
            <div className="col-span-2"><Label>Parent phone <span className="text-slate-400 text-[11px]">(optional)</span></Label><Input data-testid="student-parent-phone-input" value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="student-save-btn">{editing ? "Update" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link parent dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent data-testid="link-parent-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="w-4 h-4" /> Link parent to student</DialogTitle>
            <DialogDescription>{linkTarget?.name} · <span className="font-mono">{linkTarget?.kid_id}</span></DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Parent email<Req /></Label>
              <Input data-testid="link-parent-email-input" list="parent-emails-link" value={linkForm.parent_email} onChange={(e) => setLinkForm({ ...linkForm, parent_email: e.target.value })} placeholder="parent@example.com" className="mt-1" />
              <datalist id="parent-emails-link">
                {parents.map((p) => <option key={p.id} value={p.email}>{p.name}</option>)}
              </datalist>
              <div className="text-[11px] text-slate-500 mt-1">If no parent account exists with this email, one is created automatically.</div>
            </div>
            <div><Label>Parent name (optional)</Label><Input data-testid="link-parent-name-input" value={linkForm.parent_name} onChange={(e) => setLinkForm({ ...linkForm, parent_name: e.target.value })} className="mt-1" /></div>
            <div><Label>Parent phone (optional)</Label><Input data-testid="link-parent-phone-input" value={linkForm.parent_phone} onChange={(e) => setLinkForm({ ...linkForm, parent_phone: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button data-testid="link-parent-submit-btn" onClick={submitLink}>Link</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
